import { styled } from 'styled-components'

import { Button } from '@/components/ui/button'
import { ImageWithFallback } from '@/components/ui/image-with-fallback'
import { Typography } from '@/components/ui/typography'

import { FileUploadPreviewShape, FileUploadSize } from './file-upload.types'

// ─── Shared ──────────────────────────────────────────────────────────────────

export const Wrapper = styled.div`
  width: 100%;
`

export const HiddenInput = styled.input`
  display: none;
`

// ─── Minimized layout ────────────────────────────────────────────────────────

export const ButtonTriggerWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[8]};
`

export const SlotsRemainingText = styled(Typography)`
  margin-top: ${({ theme }) => theme.space[16]};
`

export const AcceptedFilesText = styled(Typography)`
  margin-top: ${({ theme }) => theme.space[4]};
  padding: 0 ${({ theme }) => theme.space[22]};
`

export const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[8]};
  margin-top: ${({ theme }) => theme.space[12]};
`

const FOOTER_FONT_SIZE_MAP: Record<FileUploadSize, string> = {
  large: '1.6rem',
  medium: '1.4rem',
}

const FOOTER_LINE_HEIGHT_MAP: Record<FileUploadSize, string> = {
  large: '2.4rem',
  medium: '2rem',
}

export const Footer = styled.div<{ $size: FileUploadSize }>(({ theme, $size }) => ({
  alignItems: 'flex-start',
  display: 'flex',
  fontSize: FOOTER_FONT_SIZE_MAP[$size],
  gap: theme.space[8],
  lineHeight: FOOTER_LINE_HEIGHT_MAP[$size],
  minHeight: FOOTER_LINE_HEIGHT_MAP[$size],
}))

export const Message = styled.span<{ $hasError: boolean }>(({ theme, $hasError }) => ({
  color: $hasError ? theme.colors.red500 : theme.colors.primary600,
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}))

export const MaximizedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[8]};
`

export const MaximizedTextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space[4]};
`

export const LabelBracket = styled.span`
  color: ${({ theme }) => theme.colors.black500};
  font-weight: 400;
`

export const MaximizedFileRow = styled.div`
  align-items: center;
  display: flex;
  gap: ${({ theme }) => theme.space[8]};
  height: 3.2rem;
  min-width: 0;
  width: 100%;
`

export const MaximizedErrorRow = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 3.2rem;
  margin-bottom: ${({ theme }) => theme.space[4]};
`

export const MaximizedFileName = styled(Typography)`
  flex: 1;
`

export const PreviewRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${({ theme }) => theme.space[16]};
`

export const PreviewActions = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
`

export const FullWidthButton = styled(Button)`
  width: 100%;
`

export const PreviewZoomHint = styled.div`
  align-items: center;
  background: rgba(0, 0, 0, 0.45);
  color: #fff;
  display: flex;
  inset: 0;
  justify-content: center;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  transition: opacity 0.15s ease;
`

export const PreviewBox = styled.div<{
  readonly $size: FileUploadSize
  readonly $shape?: FileUploadPreviewShape
  readonly $zoomable?: boolean
}>`
  position: relative;
  display: flex;
  flex-shrink: 0;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  background: ${({ theme }) => theme.colors.gray200};
  border-radius: ${({ theme }) => theme.border.radius[12]};
  overflow: hidden;

  ${({ $zoomable }) =>
    $zoomable &&
    `
    cursor: zoom-in;

    &:hover > ${PreviewZoomHint}, &:focus-visible > ${PreviewZoomHint} {
      opacity: 1;
    }
  `}

  ${({ $size, $shape }) =>
    $size === 'large' && $shape === 'square'
      ? `
    width: 16rem;
    height: 16rem;
    aspect-ratio: 1 / 1;
  `
      : $size === 'large'
        ? `
    width: 28rem;
    height: 16rem;
  `
        : `
    width: 10rem;
    height: 10rem;
    aspect-ratio: 1 / 1;
  `}
`

export const PreviewPlaceholder = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  padding: ${({ theme }) => theme.space[10]} 0;
  justify-content: center;
  align-items: center;
  gap: ${({ theme }) => theme.space[10]};
  box-sizing: border-box;
`

export const PreviewImg = styled(ImageWithFallback)`
  border-radius: inherit;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;

  /* Note: the broken-image fallback is a flex box - keep it, so its icon stays centered */
  &[data-component-id='ImageWithFallback'] {
    display: flex;
  }
`
